import { Stack, TextField, Card, Autocomplete, Grid, Button, Typography, IconButton, Box, Divider } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import Iconify from "../../minimals/components/iconify";
import BulletinUpload from "./BulletinUpload";
import BulletinUploadModal from "./BulletinUploadModal";
import BulletinRowData from "./BulletinRowData";
import { Bulletin, BulletinInformation, County, State, UploadInfo, BulletinLocation } from "./types";

interface Props {
  bulletinInfo: BulletinInformation;
  setBulletinInfo: (value: any) => void;
  addUpload: (value: UploadInfo, index?: number) => void;
  uploads: UploadInfo[];
  handleRemoveUpload: (index: number) => void;
  bulletinStateError: boolean;
  notes: string;
  setNotes: (value: string) => void;
  onBlurIndividual?: () => void;
  bulletinError: boolean;
  setBulletinError: (e: any) => void;
  onBlurProperty?: () => void;
  locations: Partial<BulletinLocation>[];
  setLocations: (locations: Partial<BulletinLocation>[]) => void;
  isEditMode?: boolean;
  existingBulletins?: Bulletin[];
}

export default function BulletinInfo(props: Props) {
  const [openNewUploadModal, setOpenNewUploadModal] = useState(false);
  const [states, setStates] = useState<State[]>([]);
  const [countiesByState, setCountiesByState] = useState<{ [key: number]: County[] }>({});

  const getStates = async () => {
    const { data } = await axios.get("/api/blacklist/getstates");
    setStates(data);

    const countyMap: { [key: number]: County[] } = {};
    data.forEach((state: State) => {
      countyMap[state.id] = state.counties || [];
    });
    setCountiesByState(countyMap);
  };

  const handleAddLocation = () => {
    const newLocations = [...props.locations];
    newLocations.push({ stateId: 0 });
    props.setLocations(newLocations);
  };

  const handleLocationStateChange = (index: number, state: State | null) => {
    if (!state) return;

    const newLocations = [...props.locations];
    newLocations[index] = {
      ...newLocations[index],
      stateId: state.id,
      state: state,
      countyId: undefined,
      county: undefined
    };
    props.setLocations(newLocations);

    if (props.bulletinStateError && props.locations.some((loc) => loc.stateId && loc.stateId > 0)) {
      if (props.onBlurIndividual) props.onBlurIndividual();
      if (props.onBlurProperty) props.onBlurProperty();
    }
  };

  const handleLocationCountyChange = (index: number, county: County | null) => {
    const newLocations = [...props.locations];
    newLocations[index] = {
      ...newLocations[index],
      countyId: county?.id,
      county: county || undefined
    };
    props.setLocations(newLocations);
  };

  const handleRemoveLocation = (index: number) => {
    const newLocations = [...props.locations];
    newLocations.splice(index, 1);
    props.setLocations(newLocations);
  };
  useEffect(() => {
    if (props.uploads.length > 0) {
      props.setBulletinError(false);
    }
  }, [props.uploads]);

useEffect(() => {
  getStates();
  if (props.locations.length === 0) {
    handleAddLocation();
  }
}, []);

  return (
    <Card sx={{ p: 3, maxWidth: 780, marginTop: 4 }}>
      <Typography variant="h5">Bulletin Information</Typography>
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="subtitle1" mb={2}>
          Locations
        </Typography>

        {props.bulletinStateError && (
          <Typography sx={{ color: "error.main", mb: 2 }}>At least one location with a state is required</Typography>
        )}
        {props.locations.map((location, index) => (
          <Stack key={index} direction="row" spacing={2} alignItems="center" mb={2} sx={{ position: "relative" }}>
            <Autocomplete
              options={states || []}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="State"
                  fullWidth
                  error={props.bulletinStateError && !location.stateId}
                  required
                />
              )}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => handleLocationStateChange(index, value)}
              value={location.state || null}
              sx={{ width: "40%" }}
            />

            <Autocomplete
              options={countiesByState[location.stateId || 0] || []}
              renderInput={(params) => <TextField {...params} label="County" fullWidth />}
              getOptionLabel={(option) => option.name || ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(e, value) => handleLocationCountyChange(index, value)}
              value={location.county || null}
              disabled={!location.stateId}
              sx={{ width: "40%" }}
            />

            <Box sx={{ display: "flex", width: "20%", minWidth: "90px" }}>
              <IconButton
                onClick={() => handleRemoveLocation(index)}
                disabled={props.locations.length <= 1}
                sx={{ color: "error.main" }}
              >
                <Iconify icon="eva:trash-2-outline" />
              </IconButton>

              {index === props.locations.length - 1 && (
                <Button color="primary" onClick={handleAddLocation} sx={{ ml: 1 }}>
                  <Typography sx={{ display: "flex", alignItems: "center" }}>
                    <Iconify icon="eva:plus-fill" sx={{ mr: 0.5 }} />
                    Location
                  </Typography>
                </Button>
              )}
            </Box>
          </Stack>
        ))}
      </Box>
      {props.existingBulletins && props.existingBulletins.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" mb={1}>Existing Bulletins</Typography>
          {props.existingBulletins.map((b) => (
            <BulletinRowData key={b.id} bulletin={b} />
          ))}
          <Divider sx={{ mt: 2 }} />
        </Box>
      )}
      <Stack direction={"row"} spacing={2} alignItems="center" justifyContent="space-between" gap={2}>
        <Stack>
          <Button
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="eva:upload-fill" />}
            onClick={() => setOpenNewUploadModal(true)}
          >
            Bulletin
          </Button>
          {props.bulletinError && <Typography style={{ color: "#ff5252" }}>Needs at least one bulletin</Typography>}
        </Stack>
        <Stack direction={"column"}>
          {props.uploads?.map((u, i) => (
            <Stack marginTop={2} key={i}>
              <BulletinUpload
                index={i}
                upload={u}
                onSave={props.addUpload}
                handleRemoveUpload={props.handleRemoveUpload}
                hideDelete={props.isEditMode}
              />
            </Stack>
          ))}
        </Stack>
      </Stack>
      <Grid container spacing={2} marginBottom={3} marginTop={3}>
        <Grid item xs={12}>
          <TextField
            multiline
            fullWidth
            rows={4}
            placeholder="Notes"
            onChange={(e) => props.setNotes(e.target.value)}
            value={props.notes}
            sx={{
              "& fieldset": {
                borderWidth: `1px !important`,
                borderColor: (theme) => `${theme.palette.grey[200]} !important`
              }
            }}
          />
        </Grid>
      </Grid>
      {openNewUploadModal && (
        <BulletinUploadModal
          open={openNewUploadModal}
          onClose={() => setOpenNewUploadModal(false)}
          onSave={props.addUpload}
        />
      )}
    </Card>
  );
}
